import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import AddBoxIcon from '@mui/icons-material/AddBox'
import { Box, Select, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useRequestAbort } from '~/api'
import { ActionButton, DeleteButton } from '~/components'
import { bshSlice } from '~/store'
import { compareArrays } from '~/utils'
import { useParagraphContext } from './hooks'

const assignTypes = ['and', 'or']

const Item = (props) => {
  const { assign, first, onAdd, onEdit, onRemove } = props
  const { t } = useTranslation()

  const { editable } = useParagraphContext(Item.name)

  const properties = useSelector(bshSlice.assignableProperties)

  const property = useMemo(() => {
    return properties.find((property) => property.id === assign.property_id)
  }, [assign.property_id, properties])

  return (
    <Box
      sx={{
        bgcolor: 'background.button',
        borderRadius: '4px',
        flexShrink: 0,
        px: 1.5,
        py: 0.75,
      }}
    >
      <Stack alignItems="baseline" direction="row" spacing={1}>
        <Box sx={{ width: '88px' }}>
          {first ? (
            <Typography sx={{ whiteSpace: 'nowrap' }} variant="annotation">
              {t('paragraph.assigned-to')}
            </Typography>
          ) : (
            <Select
              native
              size="small"
              sx={{
                width: '100%',
                p: 0,
              }}
              variant="standard"
              value={assign.type || ''}
              onChange={(event) => {
                onEdit({
                  ...assign,
                  type: event.target.value,
                })
              }}
            >
              {assignTypes.map((key, index) => (
                <option key={index} value={key}>
                  {t(`paragraph.assign-type-${key}`)}
                </option>
              ))}
            </Select>
          )}
        </Box>
        {editable ? (
          <>
            <Select
              native
              size="small"
              sx={{
                width: '220px',
                p: 0,
              }}
              variant="standard"
              value={assign.property_id || ''}
              onChange={(event) => {
                onEdit({
                  ...assign,
                  type: assign.type ?? (first ? null : assignTypes[0]),
                  property_id: parseInt(event.target.value),
                  property_value: null,
                })
              }}
            >
              {!assign.property_id && (
                <option value="" disabled>
                  --- brak wybranej cechy ---
                </option>
              )}
              {properties.map((property, index) => (
                <option key={index} value={property.id}>
                  {property.description}
                </option>
              ))}
            </Select>
            <Select
              disabled={!assign.property_id}
              native
              size="small"
              sx={{
                width: '220px',
                p: 0,
              }}
              variant="standard"
              value={assign.property_value || ''}
              onChange={(event) => {
                onEdit({
                  ...assign,
                  property_value: event.target.value,
                })
              }}
            >
              {!assign.property_value && (
                <option value="" disabled>
                  --- brak wartości ---
                </option>
              )}
              {property?.values.map((value, index) => (
                <option key={index} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <>
            <Box sx={{ width: '220px' }}>{property?.description}</Box>
            <Box sx={{ width: '220px' }}>{assign.property_value}</Box>
          </>
        )}
        {editable && (
          <Stack direction="row" sx={{ alignSelf: 'center' }}>
            <ActionButton icon={<AddBoxIcon />} onClick={() => onAdd()} />
            <DeleteButton disabled={!onRemove} onClick={() => onRemove()} />
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

Item.propTypes = {
  assign: PropTypes.shape({
    property_id: PropTypes.number,
    property_value: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  first: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
}

const PropertyAssigns = () => {
  const dispatch = useDispatch()
  const { editable, paragraph } = useParagraphContext(PropertyAssigns.name)

  const createAssigns = useCallback(
    (assigns, onlyValid = false) => {
      return assigns?.length
        ? [
            ...(onlyValid
              ? assigns.filter(
                  (assign) => assign.property_id && assign.property_value
                )
              : assigns),
          ]
        : onlyValid || !editable
          ? []
          : [{}]
    },
    [editable]
  )
  const [assigns, setAssigns] = useState(createAssigns(paragraph.assigns))
  useEffect(() => {
    setAssigns(createAssigns(paragraph.assigns))
  }, [createAssigns, paragraph.assigns])

  const abortController = useRequestAbort()
  useEffect(() => {
    if (!editable) return
    const validAssigns = createAssigns(assigns, true)
    const prevAssigns = createAssigns(paragraph.assigns, true)
    if (compareArrays(prevAssigns, validAssigns)) return
    ;(async () => {
      const signal = await abortController.create()
      await dispatch(
        bshSlice.updatePropertyAssigns({
          data: {
            paragraph,
            assigns: validAssigns,
          },
          signal,
        })
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigns])

  return (
    <Stack spacing={0.25}>
      {assigns.map((assign, index) => (
        <Item
          key={index}
          assign={assign}
          first={index === 0}
          onAdd={() =>
            setAssigns((assigns) => {
              assigns = [...assigns]
              assigns.push({})
              return assigns
            })
          }
          onEdit={(assign) => {
            setAssigns((assigns) => {
              assigns = [...assigns]
              assigns[index] = assign
              return assigns
            })
          }}
          onRemove={
            index > 0 || assigns.length === 1
              ? () => {
                  setAssigns((assigns) => {
                    assigns = [...assigns]
                    assigns.splice(index, 1)
                    return assigns
                  })
                }
              : undefined
          }
        />
      ))}
    </Stack>
  )
}

export default PropertyAssigns
